
import Vue from "vue";
import { UISupplierEventPublishedState, UISupplierEventStatus, UISupplierEventType } from "@/features/SupplierEvents/SupplierEvent";
import { formatDatepickerDate } from "@/shared/utils/common_utils";
import SupplierEventProduct from "@/features/SupplierEvents/SupplierEventProduct";
import SupplierEventProductModal from "@/features/SupplierEvents/SupplierEventProductModal.vue";
import AppError from "@/shared/components/AppError.vue";
import SupplierProduct from "@/features/SupplierEvents/SupplierProduct";
import Country from "@/features/SupplierEvents/Country";
import { SupplierEventType } from "@/features/SupplierEvents/SupplierEvent";
import SupplierEventProductUploadModal from "@/features/SupplierEvents/SupplierEventProductUploadModal.vue";

export default Vue.extend({
    name: "SupplierEventProductList",
    components: {
        AppError,
        SupplierEventProductModal,
        SupplierEventProductUploadModal,
    },
    props: {
        supplierEventProducts: {
            default: [] as SupplierEventProduct[],
            required: false,
        },
        editMode: {
            default: true,
            required: true,
        },
        supplierID: {
            type: String,
            required: true,
        },
        priceEvent: {
            type: Boolean,
            required: true,
        },
        type: {
            type: Number as () => SupplierEventType,
            required: true,
        },
    },
    data: () => ({
        supplierEventStatus: UISupplierEventStatus,
        supplierEventType: UISupplierEventType,
        supplierEventPublishedState: UISupplierEventPublishedState,
        errorMessage: "",
        showError: false,
        countries: [] as Country[],
        countriesLoading: false,
        supplierProducts: [] as SupplierProduct[],
        supplierProductsLoading: false,
        selectedSupplierProduct: null as null | SupplierEventProduct,
        newProduct: false,
        showModal: false,
        showBulkUploadModal: false,
    }),
    methods: {
        formatBoolsForTable(input: boolean | null): string {
            let output: string = "";
            if (input !== null) {
                output = input ? "Yes" : "No";
            }
            return output;
        },
        formatDatepickerDateForTable(dateString: string | null): string {
            let date: string = "";
            if (dateString !== null) {
                date = formatDatepickerDate(new Date(dateString));
            }
            return date;
        },
        formatPriceForTable(price: number | null): string {
            let output: string = "";
            if (price !== null) {
                output = "$" + Number(price).toFixed(2);
            }
            return output;
        },
        formatPercentForTable(percent: number | null): string {
            let output: string = "";
            if (percent !== null) {
                output = percent.toFixed(2) + "%";
            }
            return output;
        },
        async addProduct() {
            //Open modal with null product
            this.selectedSupplierProduct = null;
            this.newProduct = true;
            await this.getSupplierProducts();
            this.getCountries();
            this.showModal = true;
        },
        async editProduct(product: SupplierEventProduct) {
            //Open modal with existing product (product parameter)
            this.selectedSupplierProduct = product;
            this.newProduct = false;
            await this.getSupplierProducts();
            this.getCountries();
            this.showModal = true;
        },
        deleteProduct(id: string) {
            this.$emit("product-deleted", id);
            this.showModal = false;
        },
        async getSupplierProducts() {
            this.supplierProductsLoading = true;
            this.supplierProducts = await this.$supplierProductService.getSupplierProducts(this.supplierID);
            this.supplierProductsLoading = false;
        },
        async getCountries() {
            this.countriesLoading = true;
            this.countries = await this.$supplierEventService.getCountries();
            this.countriesLoading = false;
        },
        onModalClosed() {
            //Undo any "selected" data for the modal
            this.selectedSupplierProduct = null;
            this.showModal = false;
        },
        onProductAdded(product: SupplierEventProduct) {
            this.$emit("product-added", product);
            this.showModal = false;
        },
        onProductUpdated(product: SupplierEventProduct) {
            this.$emit("product-updated", product);
            this.showModal = false;
        },
        getProductFullName(product: SupplierEventProduct) {
            let fullName = "";
            if (product.productName != null && product.supplierName != null) {
                fullName = product.supplierName + " (" + product.productName + ")";
            } else if (product.supplierName != null) {
                fullName = product.supplierName;
            } else if (product.productName != null) {
                fullName = product.productName;
            }
            return fullName;
        },
        onBulkUploadModalClosed() {
            this.showBulkUploadModal = false;
        },
        openBulkUploadModal() {
            this.showBulkUploadModal = true;
        },
        onProductsUploaded(products: SupplierEventProduct[]) {
            this.showBulkUploadModal = false;
            this.$emit("products-added", products);
        },
    },
    computed: {
        totalSupplierEventProducts(): number {
            return this.supplierEventProducts.length;
        },
        headers(): { [key: string]: string | boolean }[] {
            const columns = [
                {
                    text: "Product",
                    value: "productName",
                    sortable: true,
                    align: "start",
                },
            ];
            if (this.priceEvent) {
                columns.push(
                    {
                        text: "Current Price",
                        value: "currentPriceUSD",
                        sortable: true,
                        align: "start",
                    },
                    {
                        text: "New Price (Proposed)",
                        value: "newProposedPriceUSD",
                        sortable: true,
                        align: "start",
                    },
                    {
                        text: "Proposed % Change",
                        value: "proposedPercentChange",
                        sortable: true,
                        align: "start",
                    },
                    {
                        text: "New Price Start Date",
                        value: "startDate",
                        sortable: true,
                        align: "start",
                    },
                    {
                        text: "Negotiated",
                        value: "wasNegotiated",
                        sortable: true,
                        align: "start",
                    },
                    {
                        text: "New Price (Final)",
                        value: "newFinalPriceUSD",
                        sortable: true,
                        align: "start",
                    },
                    {
                        text: "Final % Impact",
                        value: "finalPercentChange",
                        sortable: true,
                        align: "start",
                    }
                );
            }
            columns.push({
                text: "",
                value: "actions",
                sortable: false,
                align: "end",
            });
            return columns;
        },
    },
});
